import { v1, v4, v5, v7, validate as uuidValidate, version as uuidVersion } from 'uuid';

/**
 * UUID generator. Pure function library — page composes it; tests
 * exercise it directly without React.
 *
 * Why these versions:
 * - v1: time-based (MAC + timestamp). Useful for legacy systems.
 * - v4: random — the default everyone wants.
 * - v5: deterministic SHA-1 hash of (namespace, name). Predictable.
 * - v7: time-ordered, monotonic, sortable — RFC 9562 modern default.
 *
 * v3 (MD5) is omitted — v5 is its strict superset and we don't keep
 * MD5 in the bundle just for legacy parity.
 */

export type UuidVersion = 'v1' | 'v4' | 'v5' | 'v7';

export const UUID_VERSIONS: readonly UuidVersion[] = ['v1', 'v4', 'v5', 'v7'] as const;

export const UUID_NAMESPACES = {
  DNS: '6ba7b810-9dad-11d1-80b4-00c04fd430c8',
  URL: '6ba7b811-9dad-11d1-80b4-00c04fd430c8',
  OID: '6ba7b812-9dad-11d1-80b4-00c04fd430c8',
  X500: '6ba7b814-9dad-11d1-80b4-00c04fd430c8',
} as const;

export type UuidNamespacePreset = keyof typeof UUID_NAMESPACES | 'custom';

export interface UuidOpts {
  version: UuidVersion;
  /** [1, 1000]; values outside this range are clamped silently. */
  count: number;
  /** Required when version === 'v5'. */
  v5?: { namespace: string; name: string };
}

/** RFC 9562 §4 hex-canonical regex. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidUuid(s: string): boolean {
  return UUID_RE.test(s) && uuidValidate(s);
}

/**
 * Generate `count` UUIDs of the requested version.
 *
 * Throws when v5 inputs are missing/invalid (rather than returning a
 * `{ ok: false }` discriminated union) because the page always wraps
 * this in a try / catch and surfaces the message inline; the
 * try / catch is also where the test harness gets to assert on a
 * specific message via `expect(...).toThrow()`.
 */
export function generateUuids(opts: UuidOpts): string[] {
  const count = clamp(opts.count, 1, 1000);
  switch (opts.version) {
    case 'v1':
      return Array.from({ length: count }, () => v1());
    case 'v4':
      return Array.from({ length: count }, () => v4());
    case 'v7':
      return Array.from({ length: count }, () => v7());
    case 'v5': {
      if (!opts.v5) throw new Error('v5: 缺少 namespace 与 name');
      const { namespace, name } = opts.v5;
      if (!namespace || !isValidUuid(namespace)) {
        throw new Error('v5: namespace 必须是合法的 UUID');
      }
      if (typeof name !== 'string' || name.length === 0) {
        throw new Error('v5: name 不能为空');
      }
      return Array.from({ length: count }, () => v5(name, namespace));
    }
    default: {
      const exhaustive: never = opts.version;
      throw new Error(`unknown uuid version: ${exhaustive as string}`);
    }
  }
}

/** Extract the embedded version digit (1/4/5/7) — for tests / debug. */
export function uuidVersionOf(id: string): number | null {
  if (!isValidUuid(id)) return null;
  return uuidVersion(id);
}

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  const x = Math.floor(n);
  if (x < lo) return lo;
  if (x > hi) return hi;
  return x;
}
